import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { formatToman, formatTomanShort, toPersianDigits, } from '@bookora/shared';
import { Tooltip } from '../antd/overlays.js';
import { Numeric } from './bidi.js';
export function Money({ rials, short = false, fallback = '—', className, style, colorNegative = true, ...format }) {
    if (rials === null || rials === undefined || !Number.isFinite(rials)) {
        return _jsx("span", { className: className, children: fallback });
    }
    const negative = rials < 0;
    const body = short ? formatTomanShort(rials, format) : formatToman(rials, format);
    const composed = {
        ...(negative && colorNegative ? { color: 'var(--bk-danger-fg)' } : null),
        ...style,
    };
    if (!short) {
        return (_jsx(Numeric, { dir: "auto", className: className, style: composed, children: body }));
    }
    // A dashboard tile that says «۱٫۲ میلیارد» must still be able to answer
    // "exactly how much?" without a trip to the reports screen. The bubble is
    // for the mouse; the exact figure is also the run's `aria-description`, so
    // screen-reader, keyboard and touch users get it without hovering.
    const exact = formatToman(rials);
    return (_jsx(Tooltip, { title: exact, children: _jsx(Numeric, { dir: "auto", className: className, style: composed, "aria-description": exact, children: body }) }));
}
/** A plain integer in Persian digits with tabular figures. */
export function Num({ value, grouping = true, suffix, fallback = '—', className, style, }) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return _jsx("span", { className: className, children: fallback });
    }
    const rounded = Math.trunc(value);
    const grouped = grouping
        ? Math.abs(rounded)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
        : Math.abs(rounded).toString();
    const signed = rounded < 0 ? `−${grouped}` : grouped;
    // `auto`, not `ltr`: the suffix is usually a Persian unit word («واحد»,
    // «نفر»), which makes the whole run a Persian phrase. With no suffix there is
    // no strong character and `auto` lands on `ltr` anyway.
    return (_jsxs(Numeric, { dir: "auto", className: className, style: style, children: [toPersianDigits(signed), suffix ? _jsxs(_Fragment, { children: [" ", suffix] }) : null] }));
}
export function Percent({ value, decimals = 1, signed = false, className }) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return _jsx("span", { className: className, children: "\u2014" });
    }
    const fixed = Math.abs(value).toFixed(decimals).replace('.', '٫');
    const sign = value < 0 ? '−' : signed && value > 0 ? '+' : '';
    return (_jsxs(Numeric, { className: className, children: [sign, toPersianDigits(fixed), "\u066A"] }));
}
/** «۱٫۴ گیگابایت» — storage figures on tenant and platform screens. */
export function Bytes({ megabytes, className }) {
    if (megabytes === null || megabytes === undefined || !Number.isFinite(megabytes)) {
        return _jsx("span", { className: className, children: "\u2014" });
    }
    if (megabytes >= 1024 * 1024) {
        return (_jsxs(Numeric, { dir: "auto", className: className, children: [toPersianDigits((megabytes / 1024 / 1024).toFixed(1).replace('.', '٫')), " \u062A\u0631\u0627\u0628\u0627\u06CC\u062A"] }));
    }
    if (megabytes >= 1024) {
        return (_jsxs(Numeric, { dir: "auto", className: className, children: [toPersianDigits((megabytes / 1024).toFixed(1).replace('.', '٫')), " \u06AF\u06CC\u06AF\u0627\u0628\u0627\u06CC\u062A"] }));
    }
    return (_jsxs(Numeric, { dir: "auto", className: className, children: [toPersianDigits(Math.round(megabytes)), " \u0645\u06AF\u0627\u0628\u0627\u06CC\u062A"] }));
}
//# sourceMappingURL=money.js.map